// Generate a complementary melodic line from a source pitch pattern.
//
// Approach: walk the source MiniPattern and shift every parseable
// pitch atom by a fixed semitone interval. Atoms that don't parse as
// pitches (sample names, chord symbols, MIDI ints) are passed through
// unchanged — the caller decides whether the result is musically
// useful (it's the agent's job to refuse on a drum voice, etc.).
//
// Default interval is +4 (major third). The agent's tool layer picks
// +3 in minor keys and +7 (perfect fifth) for a safer harmonisation
// when mode is unknown. All outputs are deterministic — same input,
// same output, no RNG.

import { newBlockId, type MiniNode, type MiniPattern } from '../blocks/types';
import { formatPitch, parsePitchName, transposePitch } from './pitch';

export type CounterMelodyOptions = {
  /** Semitone shift applied to every pitch atom. Default 4 (major 3rd). */
  intervalSemitones?: number;
};

export type CounterMelodyResult = {
  pattern: MiniPattern;
  /** Atoms that looked like names but didn't parse as a pitch (chord
   *  symbols like "Cm7", MIDI ints, sample names accidentally inside a
   *  voice.note). Surfaced so callers can warn the user — a partial
   *  shift is musically incoherent. */
  skipped: string[];
};

export function buildCounterMelody(
  source: MiniPattern,
  options: CounterMelodyOptions = {},
): CounterMelodyResult {
  const interval = options.intervalSemitones ?? 4;
  const skipped: string[] = [];
  const pattern = source.map((node) => shiftNode(node, interval, skipped));
  return { pattern, skipped };
}

function shiftNode(
  node: MiniNode,
  interval: number,
  skipped: string[],
): MiniNode {
  switch (node.kind) {
    case 'atom': {
      const pitch = parsePitchName(node.name);
      if (!pitch) {
        skipped.push(node.name);
        return cloneAtom(node);
      }
      const shifted = transposePitch(pitch, interval);
      return {
        kind: 'atom',
        id: newBlockId(),
        name: formatPitch(shifted),
        ...(node.index !== undefined ? { index: node.index } : {}),
      };
    }
    case 'rest':
      return { kind: 'rest', id: newBlockId() };
    case 'repeat':
      return {
        kind: 'repeat',
        id: newBlockId(),
        inner: shiftNode(node.inner, interval, skipped),
        count: node.count,
      };
    case 'slow':
      return {
        kind: 'slow',
        id: newBlockId(),
        inner: shiftNode(node.inner, interval, skipped),
        factor: node.factor,
      };
    case 'weight':
      return {
        kind: 'weight',
        id: newBlockId(),
        inner: shiftNode(node.inner, interval, skipped),
        weight: node.weight,
      };
    case 'bang':
      return {
        kind: 'bang',
        id: newBlockId(),
        inner: shiftNode(node.inner, interval, skipped),
        count: node.count,
      };
    case 'group':
      return {
        kind: 'group',
        id: newBlockId(),
        children: node.children.map((c) => shiftNode(c, interval, skipped)),
      };
    case 'choice':
      return {
        kind: 'choice',
        id: newBlockId(),
        children: node.children.map((c) => shiftNode(c, interval, skipped)),
      };
    case 'parallel':
      return {
        kind: 'parallel',
        id: newBlockId(),
        children: node.children.map((c) => shiftNode(c, interval, skipped)),
      };
    case 'polymeter':
      return {
        kind: 'polymeter',
        id: newBlockId(),
        children: node.children.map((c) => shiftNode(c, interval, skipped)),
        ...(node.steps !== undefined ? { steps: node.steps } : {}),
      };
    case 'euclid':
      return {
        kind: 'euclid',
        id: newBlockId(),
        inner: shiftNode(node.inner, interval, skipped),
        pulses: node.pulses,
        steps: node.steps,
        ...(node.rot !== undefined ? { rot: node.rot } : {}),
      };
  }
}

function cloneAtom(node: Extract<MiniNode, { kind: 'atom' }>): MiniNode {
  return {
    kind: 'atom',
    id: newBlockId(),
    name: node.name,
    ...(node.index !== undefined ? { index: node.index } : {}),
  };
}
